import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { serverAPI, savedCommandsAPI, createServerWs } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';
import AppStatusPanel from './components/AppStatusPanel';
import FileBrowserPage from './FileBrowserPage';
import PtyTerminal from './components/PtyTerminal';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import './ServerDetailPage.css';

interface DiskMetric {
  mount: string;
  total_gb: number;
  used_gb: number;
  free_gb?: number;
  percent: number;
}

interface CurrentMetrics {
  cpu_usage_percent: number;
  cpu_load_1m: number;
  cpu_load_5m: number;
  cpu_load_15m: number;
  cpu_temperature?: number;
  memory_total_mb: number;
  memory_used_mb: number;
  memory_usage_percent: number;
  swap_total_mb: number;
  swap_used_mb: number;
  disk_metrics: DiskMetric[];
  disk_worst_percent?: number;
  network_rx_bytes: number;
  network_tx_bytes: number;
  process_count: number;
  top_processes?: ProcessInfo[];
  recorded_at: string;
}

interface Server {
  id: number;
  server_id: string;
  asset_id: number;
  asset_name?: string;
  name: string;
  server_type: 'virtual' | 'physical';
  os: string;
  os_version?: string;
  public_ip?: string;
  private_ip?: string;
  hostname?: string;
  web_server_type?: string;
  app_root_path?: string;
  status: 'online' | 'offline' | 'degraded' | 'unknown';
  last_heartbeat_at?: string;
  agent_version?: string;
  is_active: boolean;
  install_command?: string;
  created_at: string;
  updated_at: string;
}

interface MetricDataPoint {
  recorded_at: string;
  cpu_usage_percent: number;
  memory_usage_percent: number;
  disk_usage_percent?: number;
  network_rx_bytes: number | null;
  network_tx_bytes: number | null;
}

interface ServerEvent {
  id: number;
  event_type: string;
  severity: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  source: string;
  message: string;
  source_ip?: string;
  user?: string;
  metadata?: Record<string, any>;
  occurred_at: string;
}

interface SecurityEvent {
  event_type: string;
  severity: string;
  source_ip: string | null;
  count: number;
}

interface ProcessInfo {
  pid: number;
  name: string;
  cpu_pct: number;
  mem_pct: number;
  mem_mb: number;
  status: string;
}

interface RunningService {
  name: string;
  description: string;
}

interface SysInfo {
  os_name?: string;
  os_version?: string;
  kernel_version?: string;
  arch?: string;
  cpu_model?: string;
  cpu_count?: number;
  uptime_seconds?: number;
  installed_packages?: number;
  security_updates?: number;
  pending_updates?: number;
  security_packages?: Array<{ name: string; version?: string }>;
  running_services?: RunningService[];
  collected_at?: string;
}

interface RemoteCommand {
  id: number;
  command_type: string;
  command_payload?: any;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'timeout';
  result?: string;
  exit_code?: number;
  issued_by_name?: string;
  created_at: string;
  completed_at?: string;
}

interface DBConnection {
  id: number;
  name: string;
  db_type: string;
  host: string;
  port: number;
  database_name: string;
  username: string;
  status: string;
  error_message?: string;
  last_checked_at?: string;
}

interface LiveQuery {
  id: number;
  user: string;
  host: string;
  db_name: string;
  command: string;
  time_sec: number;
  state: string;
  query_text: string;
}

interface SlowQueryPattern {
  query_text: string;
  call_count: number;
  avg_time_sec: number;
  max_time_sec: number;
  avg_rows_examined: number;
  avg_rows_sent: number;
  db_name: string;
}

interface DBMetricsDetail {
  db_version?: string;
  total_size_mb?: number;
  table_count?: number;
  active_connections?: number;
  max_connections?: number;
  slow_queries?: number;
  queries_per_sec?: number;
  databases?: Array<{ name: string; size_mb: number; tables: number }>;
  live_queries?: LiveQuery[];
  slow_query_patterns?: SlowQueryPattern[];
  collected_at?: string;
}

interface DBHistoryPoint {
  active_connections: number;
  max_connections: number;
  slow_queries: number;
  queries_per_sec: number;
  total_size_mb: number;
  collected_at: string;
}

interface DashboardData {
  server: Server;
  current_metrics: CurrentMetrics;
  metrics_chart: {
    period: string;
    data: MetricDataPoint[];
  };
  recent_events: ServerEvent[];
  event_stats: Record<string, number>;
  averages: { cpu: number; memory: number };
  uptime: { last_24h: number; last_7d: number; last_30d: number };
  security_events: SecurityEvent[];
  daily_metrics: any[];
  sysinfo?: SysInfo | null;
}

interface PortResult {
  port: number;
  open: boolean;
  latency_ms: number | null;
}

const ServerDetailPage: React.FC = () => {
  useDocumentTitle('Server Details');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'metrics' | 'events' | 'access' | 'remote' | 'database' | 'config' | 'apps' | 'files'>('overview');
  const [remoteSubTab, setRemoteSubTab] = useState<'terminal' | 'history'>('terminal');
  const [terminalFullscreen, setTerminalFullscreen] = useState(false);
  const [metricsPeriod, setMetricsPeriod] = useState<'1h' | '6h' | '24h' | '7d' | '30d'>('24h');
  const [metricsData, setMetricsData] = useState<MetricDataPoint[]>([]);
  const [eventsData, setEventsData] = useState<ServerEvent[]>([]);
  const [eventsFilter, setEventsFilter] = useState<{ type?: string; severity?: string }>({});
  const [eventSearch, setEventSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [portResults, setPortResults] = useState<PortResult[] | null>(null);
  const [portChecking, setPortChecking] = useState(false);
  const [remoteCommands, setRemoteCommands] = useState<RemoteCommand[]>([]);
  const [commandLoading, setCommandLoading] = useState<string | null>(null);
  const [dbConnections, setDbConnections] = useState<DBConnection[]>([]);
  const [dbLoading, setDbLoading] = useState(false);
  const [showAddDB, setShowAddDB] = useState(false);
  const [newDB, setNewDB] = useState({ name: '', db_type: 'mysql', host: '127.0.0.1', port: 3306, database_name: '', username: '', password: '' });
  const [expandedCmd, setExpandedCmd] = useState<number | null>(null);
  // PTY message handler ref (set by PtyTerminal component)
  const ptyHandlerRef = useRef<((msg: any) => void) | null>(null);
  // DB monitoring state
  const [selectedDbConn, setSelectedDbConn] = useState<number | null>(null);
  const [dbMetricsDetail, setDbMetricsDetail] = useState<DBMetricsDetail | null>(null);
  const [dbHistory, setDbHistory] = useState<DBHistoryPoint[]>([]);
  const [dbDetailLoading, setDbDetailLoading] = useState(false);
  const [dbActiveTab, setDbActiveTab] = useState<'overview' | 'live' | 'slow'>('overview');
  // Services panel state
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceRestarting, setServiceRestarting] = useState<string | null>(null);
  // Full log modal
  const [logModal, setLogModal] = useState<{ open: boolean; title: string; content: string }>({ open: false, title: '', content: '' });
  // Access log state
  const [accessLogs, setAccessLogs] = useState<ServerEvent[]>([]);
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);
  const [accessSearch, setAccessSearch] = useState('');
  // Saved commands state
  const [savedCommands, setSavedCommands] = useState<any[]>([]);
  const [savedCmdRunning, setSavedCmdRunning] = useState<number | null>(null);
  const [showNewCmd, setShowNewCmd] = useState(false);
  const [newCmdForm, setNewCmdForm] = useState({ name: '', description: '', command_type: 'custom_command', command_payload: '{}' });
  // WebSocket connection state
  const wsRef = useRef<WebSocket | null>(null);
  const [wsConnected, setWsConnected] = useState(false);

  useEffect(() => {
    if (id) {
      loadDashboard();
      // Refresh every 30 seconds
      const interval = setInterval(loadDashboard, 30000);
      return () => clearInterval(interval);
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 'metrics') {
      loadMetrics();
    }
  }, [id, activeTab, metricsPeriod]);

  useEffect(() => {
    if (id && activeTab === 'events') {
      loadEvents();
    }
  }, [id, activeTab, eventsFilter]);

  useEffect(() => {
    if (id && activeTab === 'access') {
      loadAccessLogs();
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (id && activeTab === 'remote') {
      loadRemoteCommands();
      loadSavedCommands();
    }
  }, [id, activeTab]);

  // WebSocket connection — established once per server
  useEffect(() => {
    if (!id) return;
    const serverId = parseInt(id, 10);
    const ws = createServerWs(serverId);
    if (!ws) return;
    wsRef.current = ws;

    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'agent_status') {
          setWsConnected(msg.payload?.connected ?? false);
        } else {
          // Forward all other messages (pty_*, etc.) to the registered handler
          ptyHandlerRef.current?.(msg);
        }
      } catch {}
    };

    return () => ws.close();
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 'database') {
      loadDBConnections();
    }
  }, [id, activeTab]);

  const loadDashboard = async () => {
    try {
      const response = await serverAPI.getDashboard(id!);
      if (response.data.status === 'success') {
        setDashboardData(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to load server dashboard:', error);
      if (error.response?.status === 404) {
        toast.error('Server not found');
        navigate('/servers');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const response = await serverAPI.getMetrics(id!, metricsPeriod);
      if (response.data.status === 'success') {
        setMetricsData(response.data.data.metrics || []);
      }
    } catch (error) {
      console.error('Failed to load metrics:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await serverAPI.getEvents(id!, {
        type: eventsFilter.type as any,
        severity: eventsFilter.severity as any,
        days: 7,
        limit: 100,
      });
      if (response.data.status === 'success') {
        setEventsData(response.data.data.events || []);
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const loadAccessLogs = async () => {
    setAccessLogsLoading(true);
    try {
      // Fetch SSH login and failed login events (last 30 days)
      const [loginRes, failedRes] = await Promise.all([
        serverAPI.getEvents(id!, { type: 'ssh_login' as any, days: 30, limit: 500 }),
        serverAPI.getEvents(id!, { type: 'ssh_failed' as any, days: 30, limit: 500 }),
      ]);
      const logins = loginRes.data.status === 'success' ? (loginRes.data.data.events || []) : [];
      const failed = failedRes.data.status === 'success' ? (failedRes.data.data.events || []) : [];
      const combined = [...logins, ...failed].sort(
        (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      );
      setAccessLogs(combined);
    } catch (error) {
      console.error('Failed to load access logs:', error);
    } finally {
      setAccessLogsLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'online':
        return { color: 'success', label: 'Online', icon: 'bi-check-circle-fill' };
      case 'offline':
        return { color: 'danger', label: 'Offline', icon: 'bi-x-circle-fill' };
      case 'degraded':
        return { color: 'warning', label: 'Degraded', icon: 'bi-exclamation-triangle-fill' };
      default:
        return { color: 'secondary', label: 'Unknown', icon: 'bi-question-circle-fill' };
    }
  };

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case 'critical':
        return { color: 'danger', icon: 'bi-x-circle-fill' };
      case 'error':
        return { color: 'danger', icon: 'bi-exclamation-circle-fill' };
      case 'warning':
        return { color: 'warning', icon: 'bi-exclamation-triangle-fill' };
      case 'info':
        return { color: 'info', icon: 'bi-info-circle-fill' };
      default:
        return { color: 'secondary', icon: 'bi-dot' };
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getProgressBarColor = (value: number) => {
    if (value >= 90) return 'danger';
    if (value >= 70) return 'warning';
    return 'success';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePortCheck = async () => {
    setPortChecking(true);
    try {
      const res = await serverAPI.checkPorts(id!);
      if (res.data.status === 'success') setPortResults(res.data.data.ports);
    } catch {
      toast.error('Port check failed');
    } finally {
      setPortChecking(false);
    }
  };

  const loadRemoteCommands = async () => {
    try {
      const res = await serverAPI.listCommands(id!);
      if (res.data.status === 'success') setRemoteCommands(res.data.data.commands || []);
    } catch {
      console.error('Failed to load commands');
    }
  };

  const loadSavedCommands = async () => {
    try {
      const res = await savedCommandsAPI.list();
      setSavedCommands(res.data?.commands || []);
    } catch { /* silent */ }
  };

  const runSavedCommand = async (cmd: any) => {
    if (!id) return;
    // Shell/custom commands: type into the terminal (via PTY) or show hint
    if (cmd.command_type === 'shell_exec' || cmd.command_type === 'custom_command') {
      setRemoteSubTab('terminal');
      toast.success('Switch to the Terminal tab and type the command in the shell', { duration: 3000 });
      return;
    }
    // System commands: confirm and run directly
    if (!window.confirm(`Run "${cmd.name}" on this server?`)) return;
    setSavedCmdRunning(cmd.id);
    try {
      const res = await savedCommandsAPI.execute(cmd.id, parseInt(id, 10));
      toast.success(`"${cmd.name}" queued (id: ${res.data?.command_id})`);
      setRemoteSubTab('history');
      loadRemoteCommands();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to run "${cmd.name}"`);
    } finally {
      setSavedCmdRunning(null);
    }
  };

  const createSavedCommand = async () => {
    try {
      let payload: any = {};
      try { payload = JSON.parse(newCmdForm.command_payload); } catch {}
      await savedCommandsAPI.create({ ...newCmdForm, command_payload: payload });
      toast.success('Command saved');
      setShowNewCmd(false);
      setNewCmdForm({ name: '', description: '', command_type: 'custom_command', command_payload: '{}' });
      loadSavedCommands();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save command');
    }
  };

  const deleteSavedCommand = async (cmd: any) => {
    if (!window.confirm(`Delete "${cmd.name}"?`)) return;
    try {
      await savedCommandsAPI.delete(cmd.id);
      loadSavedCommands();
    } catch (err: any) {
      toast.error('Failed to delete command');
    }
  };

  const issueRemoteCommand = async (commandType: string, payload?: any) => {
    const labels: Record<string, string> = {
      shutdown: 'Shutdown',
      reboot: 'Reboot',
      webserver_restart: 'Web Server Restart',
      system_update: 'System Update',
      agent_update: 'Agent Update',
    };
    if (!window.confirm(`Are you sure you want to issue: ${labels[commandType] || commandType}?`)) return;
    setCommandLoading(commandType);
    try {
      const res = await serverAPI.issueCommand(id!, commandType, payload);
      const cmdId = res.data?.data?.command_id;
      toast.success(`${labels[commandType] || commandType} command issued. Waiting for agent...`);
      loadRemoteCommands();

      // Poll for result every 4s (agent polls every 30s, so result may take up to ~35s)
      if (cmdId) {
        let attempts = 0;
        const maxAttempts = 40; // ~160s max
        const poll = setInterval(async () => {
          attempts++;
          try {
            const r = await serverAPI.listCommands(id!);
            const cmds: RemoteCommand[] = r.data?.data?.commands || [];
            setRemoteCommands(cmds);
            const cmd = cmds.find((c: RemoteCommand) => c.id === cmdId);
            if (cmd && ['completed', 'failed', 'timeout'].includes(cmd.status)) {
              clearInterval(poll);
              setCommandLoading(null);
              if (cmd.status === 'completed') {
                toast.success(`${labels[commandType] || commandType} completed successfully.`);
              } else {
                toast.error(`${labels[commandType] || commandType} ${cmd.status}: ${cmd.result || 'no details'}`);
              }
              setExpandedCmd(cmdId);
            }
          } catch { /* ignore */ }
          if (attempts >= maxAttempts) {
            clearInterval(poll);
            setCommandLoading(null);
          }
        }, 4000);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to issue command');
      setCommandLoading(null);
    }
  };

  const loadDBConnections = async () => {
    setDbLoading(true);
    try {
      const res = await serverAPI.listDBConnections(id!);
      if (res.data.status === 'success') setDbConnections(res.data.data.connections || []);
    } catch {
      console.error('Failed to load DB connections');
    } finally {
      setDbLoading(false);
    }
  };

  const addDBConnection = async () => {
    if (!newDB.name || !newDB.database_name || !newDB.username) {
      toast.error('Name, database name and username are required');
      return;
    }
    try {
      await serverAPI.createDBConnection(id!, newDB);
      toast.success('DB connection added');
      setShowAddDB(false);
      setNewDB({ name: '', db_type: 'mysql', host: '127.0.0.1', port: 3306, database_name: '', username: '', password: '' });
      loadDBConnections();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add connection');
    }
  };

  const removeDBConnection = async (connId: number) => {
    if (!window.confirm('Remove this database connection?')) return;
    try {
      await serverAPI.deleteDBConnection(id!, connId);
      toast.success('Connection removed');
      loadDBConnections();
    } catch {
      toast.error('Failed to remove connection');
    }
  };

  const formatUptime = (seconds?: number) => {
    if (!seconds) return '-';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // ── DB monitoring handlers ────────────────────────────────────────────────
  const loadDbMetricsDetail = async (connId: number) => {
    setDbDetailLoading(true);
    try {
      const res = await serverAPI.getDBMetrics(id!, connId);
      if (res.data.status === 'success') {
        setDbMetricsDetail(res.data.data.latest || null);
        setDbHistory(res.data.data.history || []);
      }
    } catch {
      console.error('Failed to load DB metrics detail');
    } finally {
      setDbDetailLoading(false);
    }
  };

  const selectDbConnection = (connId: number) => {
    setSelectedDbConn(connId);
    setDbActiveTab('overview');
    setDbMetricsDetail(null);
    setDbHistory([]);
    loadDbMetricsDetail(connId);
  };

  const refreshDbLiveQueries = async () => {
    if (!selectedDbConn) return;
    // Issue a db_refresh command then reload metrics in 35s
    try {
      await serverAPI.issueCommand(id!, 'shell_exec', { command: `echo "db_refresh_${selectedDbConn}"` });
      toast.success('Live query refresh requested. Agent will update within 35s.');
      setTimeout(() => loadDbMetricsDetail(selectedDbConn), 35000);
    } catch {
      // Fallback: just reload from DB
      await loadDbMetricsDetail(selectedDbConn);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!dashboardData) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <h4>Server not found</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/servers')}>
            Back to Servers
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const { server, current_metrics, uptime } = dashboardData;
  const statusConfig = getStatusConfig(server.status);

  return (
    <>
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <button
              className="btn btn-link text-muted p-0 mb-2"
              onClick={() => navigate('/servers')}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back to servers
            </button>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className={`server-icon-lg bg-${statusConfig.color} bg-opacity-10`}>
                  <i className={`bi bi-server text-${statusConfig.color}`}></i>
                </div>
                <div>
                  <h2 className="h3 mb-1">{server.name}</h2>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`badge bg-${statusConfig.color}`}>
                      <i className={`bi ${statusConfig.icon} me-1`}></i>
                      {statusConfig.label}
                    </span>
                    <span className="text-muted small">
                      {server.os} {server.os_version}
                    </span>
                    {server.hostname && (
                      <span className="text-muted small">
                        · {server.hostname}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={loadDashboard}>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="text-muted small">CPU Usage</span>
                  <i className="bi bi-cpu text-primary"></i>
                </div>
                <div className="h3 mb-2">{current_metrics?.cpu_usage_percent?.toFixed(1) || 0}%</div>
                <div className="progress" style={{ height: '6px' }}>
                  <div
                    className={`progress-bar bg-${getProgressBarColor(current_metrics?.cpu_usage_percent || 0)}`}
                    style={{ width: `${current_metrics?.cpu_usage_percent || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="text-muted small">Memory Usage</span>
                  <i className="bi bi-memory text-info"></i>
                </div>
                <div className="h3 mb-2">{current_metrics?.memory_usage_percent?.toFixed(1) || 0}%</div>
                <div className="progress" style={{ height: '6px' }}>
                  <div
                    className={`progress-bar bg-${getProgressBarColor(current_metrics?.memory_usage_percent || 0)}`}
                    style={{ width: `${current_metrics?.memory_usage_percent || 0}%` }}
                  ></div>
                </div>
                <small className="text-muted">
                  {((current_metrics?.memory_used_mb || 0) / 1024).toFixed(1)} / {((current_metrics?.memory_total_mb || 0) / 1024).toFixed(1)} GB
                </small>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="text-muted small">Disk (Worst)</span>
                  <i className="bi bi-hdd text-warning"></i>
                </div>
                <div className="h3 mb-2">{(current_metrics?.disk_worst_percent ?? current_metrics?.disk_metrics?.[0]?.percent)?.toFixed(1) || 0}%</div>
                <div className="progress" style={{ height: '6px' }}>
                  <div
                    className={`progress-bar bg-${getProgressBarColor(current_metrics?.disk_worst_percent ?? current_metrics?.disk_metrics?.[0]?.percent ?? 0)}`}
                    style={{ width: `${current_metrics?.disk_worst_percent ?? current_metrics?.disk_metrics?.[0]?.percent ?? 0}%` }}
                  ></div>
                </div>
                <small className="text-muted">
                  {current_metrics?.disk_metrics?.length || 0} mount{(current_metrics?.disk_metrics?.length || 0) !== 1 ? 's' : ''}
                </small>
              </div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="text-muted small">Uptime (24h)</span>
                  <i className="bi bi-clock-history text-success"></i>
                </div>
                <div className="h3 mb-2">{uptime?.last_24h?.toFixed(1) || 0}%</div>
                <small className="text-muted">
                  7d: {uptime?.last_7d?.toFixed(1) || 0}% · 30d: {uptime?.last_30d?.toFixed(1) || 0}%
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4 flex-nowrap overflow-auto">
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <i className="bi bi-grid me-2"></i>
              Overview
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'metrics' ? 'active' : ''}`}
              onClick={() => setActiveTab('metrics')}
            >
              <i className="bi bi-graph-up me-2"></i>
              Metrics
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'events' ? 'active' : ''}`}
              onClick={() => setActiveTab('events')}
            >
              <i className="bi bi-list-ul me-2"></i>
              Events & Logs
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'access' ? 'active' : ''}`}
              onClick={() => setActiveTab('access')}
            >
              <i className="bi bi-shield-lock me-2"></i>
              Access Log
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'remote' ? 'active' : ''}`}
              onClick={() => setActiveTab('remote')}
            >
              <i className="bi bi-terminal me-2"></i>
              Remote Control
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'database' ? 'active' : ''}`}
              onClick={() => setActiveTab('database')}
            >
              <i className="bi bi-database me-2"></i>
              Databases
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'config' ? 'active' : ''}`}
              onClick={() => setActiveTab('config')}
            >
              <i className="bi bi-gear me-2"></i>
              Configuration
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'apps' ? 'active' : ''}`}
              onClick={() => setActiveTab('apps')}
            >
              <i className="bi bi-app me-2"></i>
              Applications
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link text-nowrap ${activeTab === 'files' ? 'active' : ''}`}
              onClick={() => setActiveTab('files')}
            >
              <i className="bi bi-folder2-open me-2"></i>
              File Manager
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="row g-4">
            {/* Server Info */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="bi bi-info-circle me-2 text-primary"></i>
                    Server Information
                  </h5>
                </div>
                <div className="card-body">
                  <table className="table table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: '40%' }}>Server ID</td>
                        <td className="fw-medium">{server.server_id}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Type</td>
                        <td className="fw-medium text-capitalize">{server.server_type}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Operating System</td>
                        <td className="fw-medium">{server.os} {server.os_version}</td>
                      </tr>
                      {server.hostname && (
                        <tr>
                          <td className="text-muted">Hostname</td>
                          <td className="fw-medium">{server.hostname}</td>
                        </tr>
                      )}
                      {server.public_ip && (
                        <tr>
                          <td className="text-muted">Public IP</td>
                          <td className="fw-medium">{server.public_ip}</td>
                        </tr>
                      )}
                      {server.private_ip && (
                        <tr>
                          <td className="text-muted">Private IP</td>
                          <td className="fw-medium">{server.private_ip}</td>
                        </tr>
                      )}
                      {server.web_server_type && (
                        <tr>
                          <td className="text-muted">Web Server</td>
                          <td className="fw-medium text-capitalize">{server.web_server_type}</td>
                        </tr>
                      )}
                      {server.app_root_path && (
                        <tr>
                          <td className="text-muted">App Root</td>
                          <td className="fw-medium"><code>{server.app_root_path}</code></td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-muted">Agent Version</td>
                        <td className="fw-medium">{server.agent_version || 'Not installed'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Last Heartbeat</td>
                        <td className="fw-medium">{server.last_heartbeat_at ? formatTime(server.last_heartbeat_at) : 'Never'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Created</td>
                        <td className="fw-medium">{formatTime(server.created_at)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* System Load */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="bi bi-speedometer2 me-2 text-primary"></i>
                    System Load
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3 mb-4">
                    <div className="col-4 text-center">
                      <div className="h4 mb-1">{current_metrics?.cpu_load_1m?.toFixed(2) || '0.00'}</div>
                      <small className="text-muted">Load 1m</small>
                    </div>
                    <div className="col-4 text-center">
                      <div className="h4 mb-1">{current_metrics?.cpu_load_5m?.toFixed(2) || '0.00'}</div>
                      <small className="text-muted">Load 5m</small>
                    </div>
                    <div className="col-4 text-center">
                      <div className="h4 mb-1">{current_metrics?.cpu_load_15m?.toFixed(2) || '0.00'}</div>
                      <small className="text-muted">Load 15m</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Processes</span>
                      <span className="fw-medium">{current_metrics?.process_count || 0}</span>
                    </div>
                    {current_metrics?.cpu_temperature != null && current_metrics.cpu_temperature > 0 && (
                      <div className="d-flex justify-content-between mb-1">
                        <span>CPU Temperature</span>
                        <span className={`fw-medium text-${current_metrics.cpu_temperature > 80 ? 'danger' : current_metrics.cpu_temperature > 60 ? 'warning' : 'success'}`}>
                          {current_metrics.cpu_temperature}°C
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Network In</span>
                      <span className="fw-medium">{formatBytes(current_metrics?.network_rx_bytes || 0)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Network Out</span>
                      <span className="fw-medium">{formatBytes(current_metrics?.network_tx_bytes || 0)}</span>
                    </div>
                  </div>

                  {current_metrics?.swap_total_mb > 0 && (
                    <div>
                      <div className="d-flex justify-content-between mb-1">
                        <span>Swap Usage</span>
                        <span className="fw-medium">
                          {current_metrics.swap_used_mb} / {current_metrics.swap_total_mb} MB
                        </span>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div
                          className="progress-bar bg-info"
                          style={{ width: `${(current_metrics.swap_used_mb / current_metrics.swap_total_mb) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Disk Partitions */}
            {current_metrics?.disk_metrics && current_metrics.disk_metrics.length > 0 && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0">
                      <i className="bi bi-hdd-stack me-2 text-primary"></i>
                      Disk Partitions
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      {current_metrics.disk_metrics.map((disk, index) => {
                        const color = getProgressBarColor(disk.percent);
                        const free = disk.free_gb ?? parseFloat((disk.total_gb - disk.used_gb).toFixed(1));
                        return (
                          <div key={index} className="col-xl-3 col-md-4 col-sm-6">
                            <div className={`p-3 rounded-3 border h-100`} style={{ borderColor: color === 'danger' ? '#dc354540' : color === 'warning' ? '#ffc10740' : '#19875440' }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <code className="fw-semibold small">{disk.mount}</code>
                                <span className={`badge bg-${color}`}>{disk.percent}%</span>
                              </div>
                              <div className="progress rounded-pill mb-2" style={{ height: '6px' }}>
                                <div className={`progress-bar bg-${color} rounded-pill`} style={{ width: `${disk.percent}%` }} />
                              </div>
                              <div className="d-flex justify-content-between" style={{ fontSize: '0.75rem' }}>
                                <span className="text-muted">{disk.used_gb} GB used</span>
                                <span className="text-muted">{free} GB free</span>
                              </div>
                              <div className="text-center mt-1" style={{ fontSize: '0.7rem', color: '#aaa' }}>
                                {disk.total_gb} GB total
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security & Port Health */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-shield-lock me-2 text-danger"></i>
                    Security (7 days)
                  </h5>
                </div>
                <div className="card-body">
                  {/* Severity summary */}
                  <div className="row g-2 mb-3">
                    {[
                      { key: 'critical', color: 'danger', icon: 'bi-x-circle-fill' },
                      { key: 'error', color: 'danger', icon: 'bi-exclamation-circle-fill' },
                      { key: 'warning', color: 'warning', icon: 'bi-exclamation-triangle-fill' },
                      { key: 'info', color: 'info', icon: 'bi-info-circle-fill' },
                    ].map(({ key, color, icon }) => (
                      dashboardData.event_stats[key] ? (
                        <div key={key} className="col-6">
                          <div className={`d-flex align-items-center gap-2 p-2 rounded-3 bg-${color} bg-opacity-10`}>
                            <i className={`bi ${icon} text-${color}`}></i>
                            <span className="small text-capitalize fw-medium">{key}</span>
                            <span className={`ms-auto badge bg-${color}`}>{dashboardData.event_stats[key]}</span>
                          </div>
                        </div>
                      ) : null
                    ))}
                    {Object.keys(dashboardData.event_stats).length === 0 && (
                      <div className="col-12 text-muted small"><i className="bi bi-check-circle text-success me-1"></i>No events in last 24h</div>
                    )}
                  </div>
                  {/* Top attacking IPs */}
                  {dashboardData.security_events?.filter(e => e.source_ip && (e.event_type === 'ssh_failed' || e.severity === 'error' || e.severity === 'critical')).slice(0, 5).map((e, i) => (
                    <div key={i} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                      <div className="small">
                        <code className="me-2">{e.source_ip}</code>
                        <span className="text-muted">{e.event_type}</span>
                      </div>
                      <span className="badge bg-danger">{e.count}×</span>
                    </div>
                  ))}
                  {dashboardData.security_events?.filter(e => e.source_ip).length === 0 && (
                    <p className="text-muted small mb-0"><i className="bi bi-shield-check text-success me-1"></i>No suspicious IPs detected</p>
                  )}
                </div>
              </div>
            </div>

            {/* Port Health */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-diagram-3 me-2 text-primary"></i>
                    Port Health
                  </h5>
                  {!portResults && (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={handlePortCheck}
                      disabled={portChecking || (!server.public_ip && !server.hostname)}
                    >
                      {portChecking ? <><span className="spinner-border spinner-border-sm me-1" />Checking...</> : <><i className="bi bi-play me-1"></i>Run Check</>}
                    </button>
                  )}
                  {portResults && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={handlePortCheck} disabled={portChecking}>
                      <i className="bi bi-arrow-clockwise me-1"></i>Re-check
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {!server.public_ip && !server.hostname && (
                    <p className="text-muted small">No public IP or hostname configured.</p>
                  )}
                  {portChecking && !portResults && (
                    <div className="text-center py-3 text-muted"><div className="spinner-border spinner-border-sm mb-2" /><p className="small mb-0">Checking ports...</p></div>
                  )}
                  {!portResults && !portChecking && (server.public_ip || server.hostname) && (
                    <p className="text-muted small mb-0">Click <strong>Run Check</strong> to test TCP reachability on ports 22, 80, 443, 8080.</p>
                  )}
                  {portResults && (
                    <div className="row g-2">
                      {portResults.map(p => (
                        <div key={p.port} className="col-6">
                          <div className={`d-flex align-items-center justify-content-between p-2 rounded-3 border border-${p.open ? 'success' : 'danger'}-subtle bg-${p.open ? 'success' : 'danger'} bg-opacity-10`}>
                            <div>
                              <span className="fw-semibold">:{p.port}</span>
                              <span className={`ms-2 small text-${p.open ? 'success' : 'danger'}`}>{p.open ? 'Open' : 'Closed'}</span>
                            </div>
                            {p.open && <small className="text-muted">{p.latency_ms}ms</small>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* System Info from Agent */}
            {dashboardData.sysinfo && (
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0">
                      <i className="bi bi-cpu me-2 text-primary"></i>
                      System Information
                    </h5>
                  </div>
                  <div className="card-body">
                    <table className="table table-borderless table-sm mb-0">
                      <tbody>
                        {dashboardData.sysinfo.os_name && (
                          <tr>
                            <td className="text-muted" style={{ width: '45%' }}>OS</td>
                            <td className="fw-medium">{dashboardData.sysinfo.os_name} {dashboardData.sysinfo.os_version}</td>
                          </tr>
                        )}
                        {dashboardData.sysinfo.kernel_version && (
                          <tr>
                            <td className="text-muted">Kernel</td>
                            <td className="fw-medium"><code className="small">{dashboardData.sysinfo.kernel_version}</code></td>
                          </tr>
                        )}
                        {dashboardData.sysinfo.arch && (
                          <tr>
                            <td className="text-muted">Architecture</td>
                            <td className="fw-medium">{dashboardData.sysinfo.arch}</td>
                          </tr>
                        )}
                        {dashboardData.sysinfo.cpu_model && (
                          <tr>
                            <td className="text-muted">CPU Model</td>
                            <td className="fw-medium small">{dashboardData.sysinfo.cpu_model} ({dashboardData.sysinfo.cpu_count} cores)</td>
                          </tr>
                        )}
                        {dashboardData.sysinfo.uptime_seconds != null && (
                          <tr>
                            <td className="text-muted">Uptime</td>
                            <td className="fw-medium">{formatUptime(dashboardData.sysinfo.uptime_seconds)}</td>
                          </tr>
                        )}
                        {dashboardData.sysinfo.installed_packages != null && (
                          <tr>
                            <td className="text-muted">Packages</td>
                            <td className="fw-medium">{dashboardData.sysinfo.installed_packages} installed</td>
                          </tr>
                        )}
                        <tr>
                          <td className="text-muted">Security Updates</td>
                          <td>
                            {dashboardData.sysinfo.security_updates! > 0 ? (
                              <span className="badge bg-danger">{dashboardData.sysinfo.security_updates} critical</span>
                            ) : (
                              <span className="badge bg-success">Up to date</span>
                            )}
                            {dashboardData.sysinfo.pending_updates! > 0 && (
                              <span className="badge bg-warning ms-1">{dashboardData.sysinfo.pending_updates} pending</span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Top Processes */}
            {current_metrics?.top_processes && current_metrics.top_processes.length > 0 && (
              <div className={dashboardData.sysinfo ? 'col-lg-6' : 'col-12'}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-0 py-3">
                    <h5 className="mb-0">
                      <i className="bi bi-list-task me-2 text-primary"></i>
                      Top Processes
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr>
                            <th className="ps-3">Process</th>
                            <th style={{ width: '90px' }}>CPU %</th>
                            <th style={{ width: '90px' }}>Mem %</th>
                            <th style={{ width: '80px' }}>Mem MB</th>
                            <th style={{ width: '80px' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {current_metrics.top_processes.slice(0, 10).map((proc, i) => (
                            <tr key={i}>
                              <td className="ps-3">
                                <span className="fw-medium">{proc.name}</span>
                                <span className="text-muted small ms-2">#{proc.pid}</span>
                              </td>
                              <td>
                                <span className={`fw-medium ${proc.cpu_pct > 50 ? 'text-danger' : proc.cpu_pct > 20 ? 'text-warning' : ''}`}>
                                  {proc.cpu_pct?.toFixed(1)}%
                                </span>
                              </td>
                              <td>{proc.mem_pct?.toFixed(1)}%</td>
                              <td>{proc.mem_mb?.toFixed(0)}</td>
                              <td>
                                <span className={`badge bg-${proc.status === 'running' ? 'success' : 'secondary'} bg-opacity-75`}>
                                  {proc.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Running Services */}
            {dashboardData.sysinfo?.running_services && dashboardData.sysinfo.running_services.length > 0 && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h5 className="mb-0">
                      <i className="bi bi-gear-wide-connected me-2 text-primary"></i>
                      Running Services
                      <span className="badge bg-secondary ms-2 fw-normal">{dashboardData.sysinfo.running_services.length}</span>
                    </h5>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ width: 200 }}
                        placeholder="Search services..."
                        value={serviceSearch}
                        onChange={e => setServiceSearch(e.target.value)}
                      />
                      <small className="text-muted text-nowrap">
                        Cached {dashboardData.sysinfo.collected_at ? new Date(dashboardData.sysinfo.collected_at).toLocaleString() : ''}
                      </small>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                      <table className="table table-sm table-hover mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Service</th>
                            <th>Description</th>
                            <th style={{ width: 100 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.sysinfo.running_services
                            .filter(s =>
                              !serviceSearch ||
                              s.name.toLowerCase().includes(serviceSearch.toLowerCase()) ||
                              s.description.toLowerCase().includes(serviceSearch.toLowerCase())
                            )
                            .map(service => (
                              <tr key={service.name}>
                                <td><code className="small">{service.name}</code></td>
                                <td className="text-muted small">{service.description}</td>
                                <td>
                                  <button
                                    className="btn btn-outline-warning btn-sm"
                                    disabled={serviceRestarting === service.name || !!commandLoading || dashboardData.server.status !== 'online'}
                                    onClick={async () => {
                                      if (!window.confirm(`Restart service: ${service.name}?`)) return;
                                      setServiceRestarting(service.name);
                                      try {
                                        await serverAPI.issueCommand(id!, 'shell_exec', { command: `systemctl restart ${service.name}` });
                                        toast.success(`Restart command sent for ${service.name}`);
                                        loadRemoteCommands();
                                        setActiveTab('remote');
                                      } catch (err: any) {
                                        toast.error(err.response?.data?.message || 'Failed to issue restart');
                                      } finally {
                                        setServiceRestarting(null);
                                      }
                                    }}
                                  >
                                    {serviceRestarting === service.name
                                      ? <span className="spinner-border spinner-border-sm" />
                                      : <><i className="bi bi-arrow-repeat me-1"></i>Restart</>
                                    }
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Events Preview */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-list-ul me-2 text-primary"></i>
                    Recent Events
                  </h5>
                  <button
                    className="btn btn-sm btn-link"
                    onClick={() => setActiveTab('events')}
                  >
                    View All
                  </button>
                </div>
                <div className="card-body p-0">
                  {dashboardData.recent_events.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No recent events
                    </div>
                  ) : (
                    <div className="list-group list-group-flush">
                      {dashboardData.recent_events.slice(0, 5).map((event) => {
                        const severityConfig = getSeverityConfig(event.severity);
                        return (
                          <div key={event.id} className="list-group-item d-flex align-items-start gap-3 px-4 py-3">
                            <i className={`bi ${severityConfig.icon} text-${severityConfig.color} mt-1`}></i>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <span className="badge bg-secondary me-2">{event.event_type}</span>
                                  <small className="text-muted">{event.source}</small>
                                </div>
                                <small className="text-muted">{formatTime(event.occurred_at)}</small>
                              </div>
                              <p className="mb-0 mt-1 small">{event.message}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'metrics' && (
          <div className="row g-4">
            {/* Period Selector */}
            <div className="col-12">
              <div className="d-flex justify-content-end mb-3">
                <div className="btn-group">
                  {(['1h', '6h', '24h', '7d', '30d'] as const).map((period) => (
                    <button
                      key={period}
                      className={`btn btn-sm ${metricsPeriod === period ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setMetricsPeriod(period)}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* CPU Chart */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3">
                  <h6 className="mb-0">CPU Usage</h6>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={metricsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="recorded_at"
                        tickFormatter={(val) => new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        fontSize={12}
                      />
                      <YAxis domain={[0, 100]} fontSize={12} />
                      <Tooltip
                        labelFormatter={(val) => new Date(val).toLocaleString()}
                        formatter={(val) => [`${Number(val || 0).toFixed(1)}%`, 'CPU']}
                      />
                      <Area
                        type="monotone"
                        dataKey="cpu_usage_percent"
                        stroke="#0d6efd"
                        fill="#0d6efd"
                        fillOpacity={0.2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Memory Chart */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3">
                  <h6 className="mb-0">Memory Usage</h6>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={metricsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="recorded_at"
                        tickFormatter={(val) => new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        fontSize={12}
                      />
                      <YAxis domain={[0, 100]} fontSize={12} />
                      <Tooltip
                        labelFormatter={(val) => new Date(val).toLocaleString()}
                        formatter={(val) => [`${Number(val || 0).toFixed(1)}%`, 'Memory']}
                      />
                      <Area
                        type="monotone"
                        dataKey="memory_usage_percent"
                        stroke="#0dcaf0"
                        fill="#0dcaf0"
                        fillOpacity={0.2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Network Chart */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3">
                  <h6 className="mb-0">Network Traffic</h6>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={metricsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="recorded_at"
                        tickFormatter={(val) => new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        fontSize={12}
                      />
                      <YAxis tickFormatter={(val) => formatBytes(val)} fontSize={12} />
                      <Tooltip
                        labelFormatter={(val) => new Date(val).toLocaleString()}
                        formatter={(val, name) => [formatBytes(Number(val || 0)), name === 'network_rx_bytes' ? 'In' : 'Out']}
                      />
                      <Line
                        type="monotone"
                        dataKey="network_rx_bytes"
                        stroke="#198754"
                        strokeWidth={2}
                        dot={false}
                        name="In"
                      />
                      <Line
                        type="monotone"
                        dataKey="network_tx_bytes"
                        stroke="#dc3545"
                        strokeWidth={2}
                        dot={false}
                        name="Out"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'remote' && (
          <div>
            {/* Sub-tab navigation */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '2px solid #e5e7eb', paddingBottom: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {(['terminal', 'history'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setRemoteSubTab(tab)}
                  style={{
                    padding: '7px 18px',
                    borderRadius: '8px',
                    border: 'none',
                    fontWeight: 500,
                    fontSize: '13px',
                    cursor: 'pointer',
                    background: remoteSubTab === tab ? '#6366f1' : '#f3f4f6',
                    color: remoteSubTab === tab ? '#fff' : '#374151',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab === 'terminal' && <><i className="bi bi-terminal me-2"></i>Terminal</>}
                  {tab === 'history' && <><i className="bi bi-clock-history me-2"></i>History</>}
                </button>
              ))}
              <div style={{ marginLeft: 'auto' }}>
                {wsConnected
                  ? <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>● Agent Connected — real-time</span>
                  : <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>● Agent Offline — polling fallback</span>
                }
              </div>
            </div>

            {/* ── Terminal sub-tab ─────────────────────────────────── */}
            {remoteSubTab === 'terminal' && (
              <>
                <PtyTerminal
                  serverId={parseInt(id!, 10)}
                  ws={wsRef.current}
                  wsConnected={wsConnected}
                  hostname={server.hostname || server.name}
                  fullscreen={terminalFullscreen}
                  onFullscreenToggle={() => setTerminalFullscreen(f => !f)}
                  onRegisterHandler={(fn) => { ptyHandlerRef.current = fn; }}
                />

              {/* ── Saved Commands (below terminal) ──────────────────── */}
              {!terminalFullscreen && (
                <div style={{ marginTop: '24px' }}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-0 fw-semibold">Saved Commands</h6>
                      <small className="text-muted">Click Run to execute · Shell commands stream in terminal above</small>
                    </div>
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setShowNewCmd(!showNewCmd)}>
                      <i className="bi bi-plus-lg me-1"></i>New
                    </button>
                  </div>

                  {showNewCmd && (
                    <div className="card border shadow-sm mb-3">
                      <div className="card-body py-3">
                        <h6 className="mb-3 small fw-semibold">Create Saved Command</h6>
                        <div className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label small fw-semibold">Name *</label>
                            <input className="form-control form-control-sm" value={newCmdForm.name} onChange={e => setNewCmdForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Clear Cache" />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small fw-semibold">Type *</label>
                            <select className="form-select form-select-sm" value={newCmdForm.command_type} onChange={e => setNewCmdForm(f => ({ ...f, command_type: e.target.value }))}>
                              <option value="custom_command">Custom Command (shell)</option>
                              <option value="shell_exec">Shell Exec</option>
                              <option value="webserver_restart">Restart Web Server</option>
                              <option value="system_update">System Update</option>
                              <option value="reboot">Reboot</option>
                              <option value="shutdown">Shutdown</option>
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small fw-semibold">Description</label>
                            <input className="form-control form-control-sm" value={newCmdForm.description} onChange={e => setNewCmdForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" />
                          </div>
                          <div className="col-12">
                            <label className="form-label small fw-semibold">Payload (JSON)</label>
                            <input className="form-control form-control-sm font-monospace" value={newCmdForm.command_payload} onChange={e => setNewCmdForm(f => ({ ...f, command_payload: e.target.value }))} placeholder='{"command": "df -h"}' />
                            <small className="text-muted">For shell/custom: {`{"command": "your-cmd"}`}</small>
                          </div>
                        </div>
                        <div className="mt-3 d-flex gap-2">
                          <button className="btn btn-sm btn-primary" onClick={createSavedCommand}>Save Command</button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowNewCmd(false)}>Cancel</button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
                    {savedCommands.map(cmd => (
                      <div
                        key={cmd.id}
                        style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', background: cmd.is_system ? '#f9fafb' : '#fff', transition: 'box-shadow 0.15s' }}
                        onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)')}
                        onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                          <div>
                            <span style={{ fontWeight: 600, fontSize: '13px' }}>{cmd.name}</span>
                            {cmd.is_system === 1 && <span className="badge bg-secondary ms-2" style={{ fontSize: '0.6rem' }}>built-in</span>}
                          </div>
                          {cmd.is_system !== 1 && (
                            <button onClick={() => deleteSavedCommand(cmd)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px', padding: '0 2px' }}>
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                        {cmd.description && <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 8px' }}>{cmd.description}</p>}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="badge bg-light text-dark border" style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                            {(cmd.command_type === 'shell_exec' || cmd.command_type === 'custom_command') ? '→ terminal' : cmd.command_type}
                          </span>
                          <button
                            onClick={() => runSavedCommand(cmd)}
                            disabled={savedCmdRunning === cmd.id}
                            style={{ padding: '4px 12px', fontSize: '12px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                          >
                            {savedCmdRunning === cmd.id ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: '0.65rem', height: '0.65rem' }} />Running...</> : 'Run'}
                          </button>
                        </div>
                      </div>
                    ))}
                    {savedCommands.length === 0 && (
                      <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#9ca3af', padding: '32px 24px' }}>
                        <i className="bi bi-bookmark display-6 d-block mb-2 opacity-50"></i>
                        No saved commands yet. Click "New" to create one.
                      </div>
                    )}
                  </div>
                </div>
              )}
              </>
            )}

            {/* ── History sub-tab ───────────────────────────────────── */}
            {remoteSubTab === 'history' && (
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-clock-history me-2 text-primary"></i>
                    Action History
                  </h5>
                  <button className="btn btn-sm btn-outline-secondary" onClick={loadRemoteCommands}>
                    <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                  </button>
                </div>
                <div className="card-body p-0">
                  {remoteCommands.filter(c => c.command_type !== 'shell_exec').length === 0 ? (
                    <div className="text-center py-4 text-muted small">No system actions issued yet.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm mb-0">
                        <thead>
                          <tr><th className="ps-3">Action</th><th>Status</th><th>Exit</th><th>Issued</th><th></th></tr>
                        </thead>
                        <tbody>
                          {remoteCommands.filter(c => c.command_type !== 'shell_exec').map(cmd => (
                            <React.Fragment key={cmd.id}>
                              <tr className={expandedCmd === cmd.id ? 'table-active' : ''}>
                                <td className="ps-3">
                                  <span className="badge bg-secondary">{cmd.command_type}</span>
                                  {cmd.command_payload?.web_server && <span className="badge bg-info bg-opacity-75 ms-1">{cmd.command_payload.web_server}</span>}
                                </td>
                                <td><span className={`badge bg-${cmd.status === 'completed' ? 'success' : cmd.status === 'failed' || cmd.status === 'timeout' ? 'danger' : cmd.status === 'running' ? 'warning' : 'secondary'}`}>{cmd.status}</span></td>
                                <td className="text-muted small">{cmd.exit_code ?? '-'}</td>
                                <td className="text-muted small text-nowrap">{formatTime(cmd.created_at)}</td>
                                <td>
                                  {cmd.result && (
                                    <button className="btn btn-sm btn-link p-0" onClick={() => setExpandedCmd(expandedCmd === cmd.id ? null : cmd.id)}>
                                      <i className={`bi bi-chevron-${expandedCmd === cmd.id ? 'up' : 'down'}`}></i>
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {expandedCmd === cmd.id && cmd.result && (
                                <tr key={`${cmd.id}-r`}>
                                  <td colSpan={5} className="p-0">
                                    <div className="position-relative">
                                      <pre className="bg-dark text-light p-3 mb-0 small" style={{ whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>{cmd.result}</pre>
                                      <button
                                        className="btn btn-sm btn-outline-light position-absolute"
                                        style={{ top: 8, right: 8, fontSize: '0.7rem', opacity: 0.8 }}
                                        onClick={() => setLogModal({ open: true, title: `${cmd.command_type} — ${formatTime(cmd.created_at)}`, content: cmd.result! })}
                                      >
                                        <i className="bi bi-arrows-fullscreen me-1"></i>Full Log
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'database' && (
          <div className="row g-4">
            {/* Left: connection list */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-database me-2 text-primary"></i>
                    Connections
                  </h5>
                  <button className="btn btn-sm btn-primary" onClick={() => setShowAddDB(!showAddDB)}>
                    <i className={`bi bi-${showAddDB ? 'x' : 'plus'}`}></i>
                  </button>
                </div>

                {showAddDB && (
                  <div className="card-body border-bottom bg-light p-3">
                    <h6 className="mb-3 small fw-semibold">Add Connection</h6>
                    <div className="d-flex flex-column gap-2">
                      <input type="text" className="form-control form-control-sm" placeholder="Name *" value={newDB.name} onChange={e => setNewDB(p => ({ ...p, name: e.target.value }))} />
                      <select className="form-select form-select-sm" value={newDB.db_type} onChange={e => setNewDB(p => ({ ...p, db_type: e.target.value, port: e.target.value === 'postgres' ? 5432 : 3306 }))}>
                        <option value="mysql">MySQL / MariaDB</option>
                        <option value="postgres">PostgreSQL</option>
                      </select>
                      <div className="input-group input-group-sm">
                        <input type="text" className="form-control" placeholder="Host" value={newDB.host} onChange={e => setNewDB(p => ({ ...p, host: e.target.value }))} />
                        <input type="number" className="form-control" style={{ maxWidth: '90px' }} placeholder="Port" value={newDB.port} onChange={e => setNewDB(p => ({ ...p, port: parseInt(e.target.value) }))} />
                      </div>
                      <input type="text" className="form-control form-control-sm" placeholder="Database name *" value={newDB.database_name} onChange={e => setNewDB(p => ({ ...p, database_name: e.target.value }))} />
                      <input type="text" className="form-control form-control-sm" placeholder="Username *" value={newDB.username} onChange={e => setNewDB(p => ({ ...p, username: e.target.value }))} />
                      <input type="password" className="form-control form-control-sm" placeholder="Password" value={newDB.password} onChange={e => setNewDB(p => ({ ...p, password: e.target.value }))} />
                      <button className="btn btn-primary btn-sm" onClick={addDBConnection}>Add Connection</button>
                      <small className="text-muted"><i className="bi bi-shield-lock me-1"></i>Password encrypted with AES-256</small>
                    </div>
                  </div>
                )}

                <div className="list-group list-group-flush">
                  {dbLoading && <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary" /></div>}
                  {!dbLoading && dbConnections.length === 0 && (
                    <div className="text-center py-4 text-muted small p-3">
                      <i className="bi bi-database display-6 d-block mb-2"></i>
                      No connections yet. Add one to start monitoring.
                    </div>
                  )}
                  {dbConnections.map(conn => (
                    <button
                      key={conn.id}
                      className={`list-group-item list-group-item-action border-0 py-3 ${selectedDbConn === conn.id ? 'active' : ''}`}
                      onClick={() => selectDbConnection(conn.id)}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <div className="fw-medium">{conn.name}</div>
                          <small className={selectedDbConn === conn.id ? 'text-white-50' : 'text-muted'}>{conn.db_type} · {conn.host}:{conn.port}</small>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1">
                          <span className={`badge bg-${conn.status === 'connected' ? 'success' : conn.status === 'error' ? 'danger' : 'secondary'}`}>
                            {conn.status}
                          </span>
                          <button
                            className={`btn btn-sm p-0 ${selectedDbConn === conn.id ? 'text-white-50' : 'text-muted'}`}
                            style={{ fontSize: '0.7rem' }}
                            onClick={e => { e.stopPropagation(); removeDBConnection(conn.id); }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                      {conn.error_message && (
                        <small className={`d-block mt-1 ${selectedDbConn === conn.id ? 'text-white-50' : 'text-danger'}`}>
                          {conn.error_message}
                        </small>
                      )}
                      {conn.last_checked_at && (
                        <small className={`d-block mt-1 ${selectedDbConn === conn.id ? 'text-white-50' : 'text-muted'}`}>
                          Checked: {formatTime(conn.last_checked_at)}
                        </small>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: DB monitoring dashboard */}
            <div className="col-lg-8">
              {!selectedDbConn ? (
                <div className="card border-0 shadow-sm h-100 d-flex align-items-center justify-content-center">
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-database display-4 mb-3 d-block"></i>
                    <p className="mb-0">Select a connection to view monitoring data</p>
                  </div>
                </div>
              ) : dbDetailLoading ? (
                <div className="card border-0 shadow-sm h-100 d-flex align-items-center justify-content-center">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-2">
                    <ul className="nav nav-tabs card-header-tabs">
                      {(['overview', 'live', 'slow'] as const).map(t => (
                        <li key={t} className="nav-item">
                          <button className={`nav-link ${dbActiveTab === t ? 'active' : ''}`} onClick={() => setDbActiveTab(t)}>
                            {t === 'overview' ? <><i className="bi bi-speedometer2 me-1"></i>Overview</> :
                             t === 'live' ? <><i className="bi bi-activity me-1"></i>Live Queries <span className="badge bg-danger ms-1">{dbMetricsDetail?.live_queries?.length || 0}</span></> :
                             <><i className="bi bi-hourglass-split me-1"></i>Slow Queries <span className="badge bg-warning ms-1">{dbMetricsDetail?.slow_query_patterns?.length || 0}</span></>}
                          </button>
                        </li>
                      ))}
                      <li className="nav-item ms-auto">
                        <button className="nav-link" onClick={() => loadDbMetricsDetail(selectedDbConn)} title="Refresh">
                          <i className="bi bi-arrow-clockwise"></i>
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="card-body">
                    {dbActiveTab === 'overview' && (
                      <>
                        {/* Key stats */}
                        {dbMetricsDetail ? (
                          <>
                            <div className="row g-3 mb-4">
                              <div className="col-6 col-md-3">
                                <div className="text-center p-3 bg-light rounded-3">
                                  <div className="h4 mb-0 text-primary">{dbMetricsDetail.active_connections ?? '-'}</div>
                                  <small className="text-muted">Active Conn.</small>
                                  {dbMetricsDetail.max_connections && (
                                    <div style={{ fontSize: '0.7rem', color: '#aaa' }}>max {dbMetricsDetail.max_connections}</div>
                                  )}
                                </div>
                              </div>
                              <div className="col-6 col-md-3">
                                <div className="text-center p-3 bg-light rounded-3">
                                  <div className="h4 mb-0 text-warning">{dbMetricsDetail.slow_queries ?? '-'}</div>
                                  <small className="text-muted">Slow Queries</small>
                                  <div style={{ fontSize: '0.7rem', color: '#aaa' }}>cumulative</div>
                                </div>
                              </div>
                              <div className="col-6 col-md-3">
                                <div className="text-center p-3 bg-light rounded-3">
                                  <div className="h4 mb-0 text-success">{dbMetricsDetail.queries_per_sec ?? '-'}</div>
                                  <small className="text-muted">QPS</small>
                                  <div style={{ fontSize: '0.7rem', color: '#aaa' }}>queries/sec</div>
                                </div>
                              </div>
                              <div className="col-6 col-md-3">
                                <div className="text-center p-3 bg-light rounded-3">
                                  <div className="h4 mb-0 text-info">{dbMetricsDetail.total_size_mb != null ? `${dbMetricsDetail.total_size_mb} MB` : '-'}</div>
                                  <small className="text-muted">Total Size</small>
                                  <div style={{ fontSize: '0.7rem', color: '#aaa' }}>{dbMetricsDetail.table_count ?? 0} tables</div>
                                </div>
                              </div>
                            </div>

                            {/* Connection usage bar */}
                            {dbMetricsDetail.max_connections && dbMetricsDetail.active_connections != null && (
                              <div className="mb-4">
                                <div className="d-flex justify-content-between mb-1 small">
                                  <span>Connection Pool Usage</span>
                                  <span>{Math.round((dbMetricsDetail.active_connections / dbMetricsDetail.max_connections) * 100)}%</span>
                                </div>
                                <div className="progress" style={{ height: '8px' }}>
                                  <div
                                    className={`progress-bar bg-${(dbMetricsDetail.active_connections / dbMetricsDetail.max_connections) > 0.8 ? 'danger' : (dbMetricsDetail.active_connections / dbMetricsDetail.max_connections) > 0.6 ? 'warning' : 'success'}`}
                                    style={{ width: `${Math.min((dbMetricsDetail.active_connections / dbMetricsDetail.max_connections) * 100, 100)}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Connections history chart */}
                            {dbHistory.length > 1 && (
                              <div className="mb-4">
                                <p className="text-muted small mb-2">Active Connections Over Time</p>
                                <ResponsiveContainer width="100%" height={150}>
                                  <AreaChart data={dbHistory}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="collected_at" tickFormatter={v => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} fontSize={11} />
                                    <YAxis fontSize={11} />
                                    <Tooltip labelFormatter={v => new Date(v).toLocaleString()} formatter={(v: any) => [v, 'Connections']} />
                                    <Area type="monotone" dataKey="active_connections" stroke="#0d6efd" fill="#0d6efd" fillOpacity={0.15} />
                                  </AreaChart>
                                </ResponsiveContainer>
                              </div>
                            )}

                            {/* Per-DB breakdown */}
                            {dbMetricsDetail.databases && dbMetricsDetail.databases.length > 0 && (
                              <div>
                                <p className="text-muted small mb-2">Databases</p>
                                <table className="table table-sm mb-0">
                                  <thead><tr><th>Database</th><th>Size (MB)</th><th>Tables</th></tr></thead>
                                  <tbody>
                                    {dbMetricsDetail.databases.map((d, i) => (
                                      <tr key={i}>
                                        <td><code>{d.name}</code></td>
                                        <td>{d.size_mb}</td>
                                        <td>{d.tables}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {dbMetricsDetail.collected_at && (
                              <p className="text-muted small mt-3 mb-0">
                                <i className="bi bi-clock me-1"></i>
                                Last collected: {formatTime(dbMetricsDetail.collected_at)} · Agent refreshes every 10 min
                              </p>
                            )}
                          </>
                        ) : (
                          <div className="text-center py-4 text-muted">
                            <i className="bi bi-clock-history d-block display-6 mb-2"></i>
                            <p className="mb-0">No metrics collected yet. Agent connects every 10 minutes.</p>
                          </div>
                        )}
                      </>
                    )}

                    {dbActiveTab === 'live' && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h6 className="mb-0">Live Queries</h6>
                            <small className="text-muted">Currently running queries on the database server</small>
                          </div>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => loadDbMetricsDetail(selectedDbConn)}>
                            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                          </button>
                        </div>
                        {!dbMetricsDetail?.live_queries?.length ? (
                          <div className="text-center py-4 text-muted">
                            <i className="bi bi-check-circle text-success d-block display-6 mb-2"></i>
                            <p className="mb-0">No active queries at last collection</p>
                            <small>Data refreshes with each agent collection cycle (~10 min)</small>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                              <thead>
                                <tr>
                                  <th>PID</th>
                                  <th>User</th>
                                  <th>DB</th>
                                  <th>Time (s)</th>
                                  <th>State</th>
                                  <th>Query</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dbMetricsDetail.live_queries.map((q, i) => (
                                  <tr key={i}>
                                    <td className="text-muted small">{q.id}</td>
                                    <td className="small">{q.user}</td>
                                    <td className="small"><code>{q.db_name}</code></td>
                                    <td>
                                      <span className={`fw-medium ${q.time_sec > 30 ? 'text-danger' : q.time_sec > 5 ? 'text-warning' : 'text-success'}`}>
                                        {q.time_sec}s
                                      </span>
                                    </td>
                                    <td className="small text-muted">{q.state || q.command}</td>
                                    <td className="small">
                                      <code className="text-truncate d-inline-block" style={{ maxWidth: '250px' }} title={q.query_text}>
                                        {q.query_text}
                                      </code>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {dbMetricsDetail?.collected_at && (
                          <p className="text-muted small mt-2 mb-0">Snapshot from: {formatTime(dbMetricsDetail.collected_at)}</p>
                        )}
                      </>
                    )}

                    {dbActiveTab === 'slow' && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h6 className="mb-0">Slow Query Analysis</h6>
                            <small className="text-muted">
                              MySQL: from <code>performance_schema</code> · PostgreSQL: from <code>pg_stat_statements</code>
                            </small>
                          </div>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => loadDbMetricsDetail(selectedDbConn)}>
                            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                          </button>
                        </div>
                        {!dbMetricsDetail?.slow_query_patterns?.length ? (
                          <div className="text-center py-4 text-muted">
                            <i className="bi bi-check-circle text-success d-block display-6 mb-2"></i>
                            <p className="mb-0">No slow queries detected</p>
                            <small>Enable <code>performance_schema</code> (MySQL) or <code>pg_stat_statements</code> (PostgreSQL) for query analysis</small>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                              <thead>
                                <tr>
                                  <th>Query</th>
                                  <th style={{ width: '80px' }}>Calls</th>
                                  <th style={{ width: '90px' }}>Avg (s)</th>
                                  <th style={{ width: '90px' }}>Max (s)</th>
                                  <th style={{ width: '90px' }}>Avg Rows</th>
                                  <th style={{ width: '80px' }}>DB</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dbMetricsDetail.slow_query_patterns.map((p, i) => (
                                  <tr key={i}>
                                    <td>
                                      <code className="small text-truncate d-inline-block" style={{ maxWidth: '300px' }} title={p.query_text}>
                                        {p.query_text}
                                      </code>
                                    </td>
                                    <td className="small">{p.call_count}</td>
                                    <td>
                                      <span className={`fw-medium small ${p.avg_time_sec > 5 ? 'text-danger' : p.avg_time_sec > 1 ? 'text-warning' : 'text-success'}`}>
                                        {p.avg_time_sec?.toFixed(3)}s
                                      </span>
                                    </td>
                                    <td className="small text-muted">{p.max_time_sec?.toFixed(3)}s</td>
                                    <td className="small text-muted">{Math.round(p.avg_rows_examined)}</td>
                                    <td className="small text-muted"><code>{p.db_name}</code></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {dbMetricsDetail?.collected_at && (
                          <p className="text-muted small mt-2 mb-0">Snapshot from: {formatTime(dbMetricsDetail.collected_at)}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'events' && (() => {
          const q = eventSearch.toLowerCase();
          const filtered = eventsData.filter(e =>
            !q ||
            e.message?.toLowerCase().includes(q) ||
            e.source_ip?.toLowerCase().includes(q) ||
            e.user?.toLowerCase().includes(q) ||
            e.source?.toLowerCase().includes(q) ||
            e.event_type?.toLowerCase().includes(q)
          );
          return (
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                  <h5 className="mb-0">Events & Logs <small className="text-muted fw-normal">({filtered.length}{eventSearch ? ` of ${eventsData.length}` : ''})</small></h5>
                  <div className="d-flex gap-2 flex-wrap">
                    <div className="input-group input-group-sm" style={{ minWidth: '200px' }}>
                      <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                      <input
                        type="text"
                        className="form-control border-start-0 ps-0"
                        placeholder="Search message, IP, user..."
                        value={eventSearch}
                        onChange={(e) => setEventSearch(e.target.value)}
                      />
                      {eventSearch && (
                        <button className="btn btn-outline-secondary" onClick={() => setEventSearch('')}>
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                    <select
                      className="form-select form-select-sm"
                      value={eventsFilter.type || ''}
                      onChange={(e) => setEventsFilter(prev => ({ ...prev, type: e.target.value || undefined }))}
                    >
                      <option value="">All Types</option>
                      <option value="ssh_login">SSH Login</option>
                      <option value="ssh_failed">SSH Failed</option>
                      <option value="web_access">Web Access</option>
                      <option value="web_error">Web Error</option>
                      <option value="app_log">App Log</option>
                      <option value="system">System</option>
                      <option value="security">Security</option>
                    </select>
                    <select
                      className="form-select form-select-sm"
                      value={eventsFilter.severity || ''}
                      onChange={(e) => setEventsFilter(prev => ({ ...prev, severity: e.target.value || undefined }))}
                    >
                      <option value="">All Severities</option>
                      <option value="critical">Critical</option>
                      <option value="error">Error</option>
                      <option value="warning">Warning</option>
                      <option value="info">Info</option>
                      <option value="debug">Debug</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                {filtered.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-inbox display-4 mb-3"></i>
                    {eventSearch ? (
                      <p>No events match your search</p>
                    ) : (
                      <div>
                        <p className="fw-semibold mb-1">No events collected yet</p>
                        <p className="small text-muted mb-2">
                          The agent collects SSH logins, failed login attempts, and web server access/errors.<br />
                          It requires read access to <code>/var/log/auth.log</code> and your web server logs.
                        </p>
                        <p className="small text-muted">
                          Check agent logs: <code>journalctl -u cloudayn-agent</code>
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}></th>
                          <th>Type</th>
                          <th>Source</th>
                          <th>Message</th>
                          <th>IP / User</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((event) => {
                          const severityConfig = getSeverityConfig(event.severity);
                          return (
                            <tr key={event.id}>
                              <td className="text-center">
                                <i className={`bi ${severityConfig.icon} text-${severityConfig.color}`}></i>
                              </td>
                              <td><span className="badge bg-secondary">{event.event_type}</span></td>
                              <td className="text-muted small">{event.source}</td>
                              <td
                                style={{ cursor: event.message ? 'pointer' : 'default', maxWidth: '340px' }}
                                title={event.message || undefined}
                                onClick={() => event.message && setLogModal({ open: true, title: `${event.event_type} — ${formatTime(event.occurred_at)}`, content: event.message })}
                              >
                                <span
                                  className="text-truncate d-inline-block"
                                  style={{ maxWidth: '300px', verticalAlign: 'middle' }}
                                >
                                  {event.message}
                                </span>
                                {event.message && event.message.length > 80 && (
                                  <i className="bi bi-arrows-fullscreen ms-1 text-muted" style={{ fontSize: '10px', verticalAlign: 'middle' }}></i>
                                )}
                              </td>
                              <td className="small">
                                {event.source_ip && <div>{event.source_ip}</div>}
                                {event.user && <div className="text-muted">{event.user}</div>}
                              </td>
                              <td className="text-muted small text-nowrap">{formatTime(event.occurred_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Access Log Tab ─────────────────────────────────── */}
        {activeTab === 'access' && (() => {
          const filteredAccess = accessLogs.filter(e => {
            if (!accessSearch) return true;
            const q = accessSearch.toLowerCase();
            return (
              (e.source_ip || '').toLowerCase().includes(q) ||
              (e.user || '').toLowerCase().includes(q) ||
              (e.message || '').toLowerCase().includes(q)
            );
          });
          const successCount = accessLogs.filter(e => e.event_type === 'ssh_login').length;
          const failedCount  = accessLogs.filter(e => e.event_type === 'ssh_failed').length;
          return (
            <div>
              {/* Stats row */}
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm text-center py-3">
                    <div className="h3 fw-bold text-primary mb-0">{accessLogs.length}</div>
                    <div className="small text-muted">Total Events (30d)</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm text-center py-3">
                    <div className="h3 fw-bold text-success mb-0">{successCount}</div>
                    <div className="small text-muted">Successful Logins</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm text-center py-3">
                    <div className="h3 fw-bold text-danger mb-0">{failedCount}</div>
                    <div className="small text-muted">Failed Attempts</div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm text-center py-3">
                    <div className="h3 fw-bold text-warning mb-0">
                      {new Set(accessLogs.filter(e => e.source_ip).map(e => e.source_ip)).size}
                    </div>
                    <div className="small text-muted">Unique IPs</div>
                  </div>
                </div>
              </div>

              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">
                    <i className="bi bi-shield-lock me-2 text-primary"></i>
                    SSH Access Log
                    <span className="badge bg-secondary ms-2" style={{ fontSize: '0.75rem' }}>{filteredAccess.length}</span>
                  </h5>
                  <div className="d-flex gap-2 align-items-center">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search IP, user…"
                      value={accessSearch}
                      onChange={e => setAccessSearch(e.target.value)}
                      style={{ width: 180 }}
                    />
                    <button className="btn btn-sm btn-outline-secondary" onClick={loadAccessLogs}>
                      <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                    </button>
                  </div>
                </div>
                <div className="card-body p-0">
                  {accessLogsLoading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                  ) : filteredAccess.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bi bi-shield-check display-4 mb-3 d-block"></i>
                      <p>{accessSearch ? 'No entries match your search' : 'No SSH login events found in the last 30 days'}</p>
                      <small>The agent reports SSH events from /var/log/auth.log</small>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 40 }}></th>
                            <th>Status</th>
                            <th>IP Address</th>
                            <th>User</th>
                            <th>Message</th>
                            <th>Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAccess.map(event => {
                            const isSuccess = event.event_type === 'ssh_login';
                            return (
                              <tr key={event.id} className={isSuccess ? '' : 'table-danger table-danger-subtle'}>
                                <td className="text-center">
                                  <i className={`bi ${isSuccess ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'}`}></i>
                                </td>
                                <td>
                                  <span className={`badge ${isSuccess ? 'bg-success' : 'bg-danger'}`}>
                                    {isSuccess ? 'Accepted' : 'Failed'}
                                  </span>
                                </td>
                                <td className="small fw-semibold font-monospace">{event.source_ip || '—'}</td>
                                <td className="small">{event.user || '—'}</td>
                                <td className="small text-muted" style={{ maxWidth: 280 }}>
                                  <span className="text-truncate d-inline-block" style={{ maxWidth: 280 }} title={event.message}>
                                    {event.message}
                                  </span>
                                </td>
                                <td className="small text-muted text-nowrap">{formatTime(event.occurred_at)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'config' && (
          <div className="row g-4">
            <div className="col-lg-8">
              {/* Installation Instructions */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="bi bi-terminal me-2 text-primary"></i>
                    Agent Installation - Linux
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-4">
                    <h6 className="text-muted mb-3">Step 1: SSH into your server</h6>
                    <p className="text-muted small">Connect to your server via SSH:</p>
                    <div className="bg-dark text-light p-3 rounded position-relative">
                      <code>ssh root@{server.public_ip || 'your-server-ip'}</code>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-muted mb-3">Step 2: Run the installation command</h6>
                    <p className="text-muted small">
                      Copy and paste this command in your server terminal. This will download and install the CloudAyn Agent:
                    </p>
                    <div className="bg-dark text-light p-3 rounded position-relative">
                      <pre className="mb-0 text-wrap" style={{ fontSize: '0.85rem' }}>
                        <code>{server.install_command || 'Loading...'}</code>
                      </pre>
                      <button
                        className="btn btn-sm btn-outline-light position-absolute"
                        style={{ top: '8px', right: '8px' }}
                        onClick={() => copyToClipboard(server.install_command || '')}
                      >
                        <i className={`bi ${copied ? 'bi-check' : 'bi-clipboard'} me-1`}></i>
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h6 className="text-muted mb-3">Step 3: Verify installation</h6>
                    <p className="text-muted small">Check if the agent is running:</p>
                    <div className="bg-dark text-light p-3 rounded">
                      <code>sudo systemctl status cloudayn-agent</code>
                    </div>
                  </div>

                  <div className="mb-0">
                    <h6 className="text-muted mb-3">Step 4: Monitor your server</h6>
                    <p className="text-muted small mb-0">
                      Once the agent is installed, it will automatically start sending metrics.
                      Check the Overview tab to see your server's real-time data.
                    </p>
                  </div>
                </div>
              </div>

              {/* Manual Installation */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-gear me-2 text-secondary"></i>
                    Manual Installation (Advanced)
                  </h5>
                  <span className="badge bg-secondary">Optional</span>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-3">
                    If automatic installation doesn't work, you can manually install the agent:
                  </p>

                  <div className="accordion" id="manualInstall">
                    <div className="accordion-item border-0">
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button collapsed bg-light"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#debianInstall"
                        >
                          <i className="bi bi-ubuntu me-2"></i>
                          Debian/Ubuntu
                        </button>
                      </h2>
                      <div id="debianInstall" className="accordion-collapse collapse" data-bs-parent="#manualInstall">
                        <div className="accordion-body">
                          <div className="bg-dark text-light p-3 rounded small">
                            <pre className="mb-0">
{`# Download the agent
wget https://github.com/cloudayn/agent/releases/latest/download/cloudayn-agent-linux-amd64.deb

# Install the package
sudo dpkg -i cloudayn-agent-linux-amd64.deb

# Configure the agent
sudo nano /etc/cloudayn-agent/config.yaml

# Start the service
sudo systemctl enable cloudayn-agent
sudo systemctl start cloudayn-agent`}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="accordion-item border-0">
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button collapsed bg-light"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#rhelInstall"
                        >
                          <i className="bi bi-box me-2"></i>
                          RHEL/CentOS/Fedora
                        </button>
                      </h2>
                      <div id="rhelInstall" className="accordion-collapse collapse" data-bs-parent="#manualInstall">
                        <div className="accordion-body">
                          <div className="bg-dark text-light p-3 rounded small">
                            <pre className="mb-0">
{`# Download the agent
curl -LO https://github.com/cloudayn/agent/releases/latest/download/cloudayn-agent-linux-amd64.rpm

# Install the package
sudo rpm -i cloudayn-agent-linux-amd64.rpm

# Configure and start
sudo systemctl enable cloudayn-agent
sudo systemctl start cloudayn-agent`}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="accordion-item border-0">
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button collapsed bg-light"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#configFile"
                        >
                          <i className="bi bi-file-code me-2"></i>
                          Configuration File
                        </button>
                      </h2>
                      <div id="configFile" className="accordion-collapse collapse" data-bs-parent="#manualInstall">
                        <div className="accordion-body">
                          <p className="small text-muted">Create <code>/etc/cloudayn-agent/config.yaml</code>:</p>
                          <div className="bg-dark text-light p-3 rounded small">
                            <pre className="mb-0">
{`# CloudAyn Agent Configuration
api_endpoint: "${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/v1"
server_id: "${server.server_id}"

# Metrics collection interval (seconds)
metrics_interval: 60

# Log collection settings
log_paths:
  - /var/log/syslog
  - /var/log/auth.log
  - /var/log/nginx/access.log
  - /var/log/nginx/error.log

# Web server type
web_server: "${server.web_server_type || 'nginx'}"

# Application root path
app_root: "${server.app_root_path || '/var/www/html'}"`}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remove & Reinstall */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-arrow-repeat me-2 text-warning"></i>
                    Remove &amp; Reinstall Agent
                  </h5>
                  <span className="badge bg-warning text-dark">Advanced</span>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-3">
                    Use the commands below to completely remove the agent and reinstall it fresh. Useful when the agent is not sending data or behaving unexpectedly.
                  </p>
                  <div className="accordion" id="reinstallAccordion">
                    <div className="accordion-item border-0">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#removeStep">
                          <i className="bi bi-trash me-2 text-danger"></i>
                          Step 1: Remove Existing Agent
                        </button>
                      </h2>
                      <div id="removeStep" className="accordion-collapse collapse" data-bs-parent="#reinstallAccordion">
                        <div className="accordion-body">
                          <div className="bg-dark text-light p-3 rounded position-relative">
                            <button
                              className="btn btn-sm btn-outline-light position-absolute"
                              style={{ top: '8px', right: '8px', fontSize: '0.7rem' }}
                              onClick={() => copyToClipboard(`# Stop the agent service\nsudo systemctl stop cloudayn-agent\n\n# Disable auto-start on boot\nsudo systemctl disable cloudayn-agent\n\n# Remove the installed package (Debian/Ubuntu)\nsudo apt-get remove --purge cloudayn-agent -y 2>/dev/null || true\n\n# Remove RHEL/CentOS package if applicable\nsudo rpm -e cloudayn-agent 2>/dev/null || true\n\n# Remove configuration directory\nsudo rm -rf /etc/cloudayn-agent\n\n# Remove binary (if manually installed)\nsudo rm -f /usr/local/bin/cloudayn-agent\n\n# Remove the systemd service file (if manually installed)\nsudo rm -f /etc/systemd/system/cloudayn-agent.service\n\n# Reload systemd daemon to forget the removed service\nsudo systemctl daemon-reload`)}
                            >
                              <i className="bi bi-clipboard me-1"></i>Copy
                            </button>
                            <pre className="mb-0 small">
{`# Stop the agent service
sudo systemctl stop cloudayn-agent

# Disable auto-start on boot
sudo systemctl disable cloudayn-agent

# Remove the installed package (Debian/Ubuntu)
sudo apt-get remove --purge cloudayn-agent -y 2>/dev/null || true

# Remove RHEL/CentOS package if applicable
sudo rpm -e cloudayn-agent 2>/dev/null || true

# Remove configuration directory
sudo rm -rf /etc/cloudayn-agent

# Remove binary (if manually installed)
sudo rm -f /usr/local/bin/cloudayn-agent

# Remove the systemd service file (if manually installed)
sudo rm -f /etc/systemd/system/cloudayn-agent.service

# Reload systemd daemon to forget the removed service
sudo systemctl daemon-reload`}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="accordion-item border-0 mt-1">
                      <h2 className="accordion-header">
                        <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#reinstallStep">
                          <i className="bi bi-download me-2 text-success"></i>
                          Step 2: Reinstall Agent
                        </button>
                      </h2>
                      <div id="reinstallStep" className="accordion-collapse collapse" data-bs-parent="#reinstallAccordion">
                        <div className="accordion-body">
                          <p className="small text-muted mb-2">Run the one-click install command for this server:</p>
                          <div className="bg-dark text-light p-3 rounded position-relative">
                            <button
                              className="btn btn-sm btn-outline-light position-absolute"
                              style={{ top: '8px', right: '8px', fontSize: '0.7rem' }}
                              onClick={() => copyToClipboard(server.install_command || '')}
                            >
                              <i className={`bi ${copied ? 'bi-check' : 'bi-clipboard'} me-1`}></i>
                              {copied ? 'Copied!' : 'Copy'}
                            </button>
                            <pre className="mb-0 small text-wrap">
                              <code>{server.install_command || 'Loading...'}</code>
                            </pre>
                          </div>
                          <p className="small text-muted mt-2 mb-0">
                            <i className="bi bi-info-circle me-1"></i>
                            After reinstall, verify with: <code>sudo systemctl status cloudayn-agent</code>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Server Settings */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="bi bi-sliders me-2 text-primary"></i>
                    Server Settings
                  </h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Server Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={server.name}
                      disabled
                    />
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Public IP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={server.public_ip || ''}
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Private IP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={server.private_ip || ''}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                    <div>
                      <div className="fw-medium">Server Active</div>
                      <small className="text-muted">Disable to stop monitoring this server</small>
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={server.is_active}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h6 className="text-primary mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Agent Information
                  </h6>
                  <table className="table table-sm table-borderless mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted">Status</td>
                        <td>
                          <span className={`badge bg-${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Version</td>
                        <td className="fw-medium">{server.agent_version || 'Not installed'}</td>
                      </tr>
                      <tr>
                        <td className="text-muted">Last Heartbeat</td>
                        <td className="fw-medium">
                          {server.last_heartbeat_at ? formatTime(server.last_heartbeat_at) : 'Never'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card border-0 bg-danger bg-opacity-10 mt-4">
                <div className="card-body">
                  <h6 className="text-danger mb-2">
                    <i className="bi bi-trash me-2"></i>
                    Danger Zone
                  </h6>
                  <p className="small mb-3 text-muted">
                    Deleting this server will remove all monitoring data and cannot be undone.
                  </p>
                  <button className="btn btn-outline-danger btn-sm">
                    Delete Server
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ── Applications Tab ──────────────────────────────── */}
        {activeTab === 'apps' && (
          <div style={{ padding: '8px 0' }}>
            <AppStatusPanel serverId={parseInt(id!, 10)} wsConnected={wsConnected} />
          </div>
        )}

        {/* ── File Manager Tab ──────────────────────────────── */}
        {activeTab === 'files' && (
          <FileBrowserPage standalone={false} serverId={parseInt(id!, 10)} />
        )}

      </div>
    </DashboardLayout>

    {/* ── Full Log Modal ───────────────────────────────────── */}
    {logModal.open && (

      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        onClick={e => { if (e.target === e.currentTarget) setLogModal(m => ({ ...m, open: false })); }}
      >
        <div style={{ background: '#1a1a2e', borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #333', flexShrink: 0 }}>
            <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#a0aec0' }}>
              <i className="bi bi-terminal me-2 text-success"></i>
              {logModal.title}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-light"
                style={{ fontSize: '0.75rem' }}
                onClick={() => { navigator.clipboard.writeText(logModal.content); toast.success('Copied!'); }}
              >
                <i className="bi bi-clipboard me-1"></i>Copy
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setLogModal(m => ({ ...m, open: false }))}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
          {/* Content */}
          <pre style={{ flex: 1, overflowY: 'auto', margin: 0, padding: '16px 20px', fontFamily: 'monospace', fontSize: '0.82rem', color: '#e2e8f0', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6 }}>
            {logModal.content}
          </pre>
          {/* Footer */}
          <div style={{ padding: '10px 20px', borderTop: '1px solid #333', flexShrink: 0, textAlign: 'right' }}>
            <small style={{ color: '#718096' }}>{logModal.content.split('\n').length} lines · {logModal.content.length} chars</small>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ServerDetailPage;
